import { setupSocketCaseConverter } from "@/lib/socketCaseConverter";
import { type Socket, io } from "socket.io-client";

// List of namespaces the frontend currently uses. If you add more on the server, extend this.
export type SocketNamespace =
  | "charting"
  | "chat"
  | "universal-clinic-chat"
  | "distribution";

export type SocketMap = Record<SocketNamespace, Socket>;

/**
 * SocketManager wraps socket.io-client so that we:
 * 1. Establish ONE physical connection shared across our namespaces
 * 2. Expose strongly-typed sockets per namespace
 * 3. Handle token refresh & logout in one place
 */
class SocketManager {
  private static _instance: SocketManager;
  private sockets: Partial<Record<SocketNamespace, Socket>> = {};
  private readonly host = import.meta.env.FO_NEW_CHAT_HOST as string;

  // Singleton accessor
  public static get instance(): SocketManager {
    if (!SocketManager._instance) {
      SocketManager._instance = new SocketManager();
    }
    return SocketManager._instance;
  }

  /**
   * Connects to all configured namespaces. Subsequent calls are no-ops unless
   * `forceNew` is true or no sockets exist.
   */
  public connect(token: string, { forceNew = false } = {}): SocketMap {
    if (!forceNew && Object.values(this.sockets).length === 4) {
      // Already connected to every namespace.
      return this.sockets as SocketMap;
    }

    if (forceNew) {
      this.disconnect();
    }

    const commonOptions = {
      withCredentials: true,
      transportOptions: {
        polling: {
          extraHeaders: {
            Authorization: `Bearer ${token}`,
          },
        },
      },
      // forceNew only when explicitly requested (e.g., token refresh)
      forceNew,
    } as const;

    // Initialise each namespace socket if it does not already exist
    const namespaces: SocketNamespace[] = [
      "charting",
      "universal-clinic-chat",
      "chat",
      "distribution",
    ];
    for (const ns of namespaces) {
      if (!this.sockets[ns]) {
        const s = io(`${this.host}/${ns}`, commonOptions);
        setupSocketCaseConverter(s);
        this.sockets[ns] = s;
      }
    }

    return this.sockets as SocketMap;
  }

  /**
   * Disconnects every namespace socket and clears internal references.
   */
  public disconnect(): void {
    for (const socket of Object.values(this.sockets)) {
      try {
        socket?.removeAllListeners();
        socket?.disconnect();
      } catch {
        /* noop */
      }
    }
    this.sockets = {};
  }

  /**
   * Refreshes the auth token by forcing a reconnection with the new token.
   */
  public refreshToken(token: string): SocketMap {
    return this.connect(token, { forceNew: true });
  }

  /**
   * Retrieve a socket by namespace. Returns undefined if not yet connected.
   */
  public get(namespace: SocketNamespace): Socket | undefined {
    return this.sockets[namespace];
  }
}

export default SocketManager.instance;

// ----------------------------------------------------------------------------
// Functional façade – preferred public API
// ----------------------------------------------------------------------------
export function initSockets(token: string): SocketMap {
  return SocketManager.instance.connect(token);
}

export function refreshSockets(token: string): SocketMap {
  return SocketManager.instance.refreshToken(token);
}

export function disconnectAll(): void {
  SocketManager.instance.disconnect();
}
